import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const ENTITY_TYPES = [
  { value: 'us_llc', label: 'US LLC' },
  { value: 'us_c_corp', label: 'US C-Corp' },
  { value: 'us_s_corp', label: 'US S-Corp' },
  { value: 'us_sole_proprietorship', label: 'US Sole Proprietorship' },
];

const AddCompanyPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    company_name: '',
    entity_type: '',
    state_of_formation: '',
    date_of_formation: '',
    business_address: { street: '', city: '', state: '', zip: '' },
    mailing_address: { street: '', city: '', state: '', zip: '' },
  });

  const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Sync mailing address when checkbox is on
  useEffect(() => {
    if (sameAsBusinessAddress) {
      setFormData(prev => ({
        ...prev,
        mailing_address: { ...prev.business_address }
      }));
    }
  }, [sameAsBusinessAddress, formData.business_address]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: { ...prev[addressType], [field]: value }
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    if (!formData.entity_type) newErrors.entity_type = 'Entity type is required';
    if (!formData.state_of_formation) newErrors.state_of_formation = 'State of formation is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        company_name: formData.company_name.trim(),
        entity_type: formData.entity_type,
        state_of_formation: formData.state_of_formation,
        country: 'US',
      };
      if (formData.date_of_formation) payload.date_of_formation = formData.date_of_formation;

      const ba = formData.business_address;
      if (ba.street || ba.city || ba.state || ba.zip) {
        payload.business_address = {
          street: ba.street || '', city: ba.city || '', state: ba.state || '', zip: ba.zip || '', country: 'US'
        };
      }

      const ma = sameAsBusinessAddress ? formData.business_address : formData.mailing_address;
      if (ma.street || ma.city || ma.state || ma.zip) {
        payload.mailing_address = {
          street: ma.street || '', city: ma.city || '', state: ma.state || '', zip: ma.zip || '', country: 'US'
        };
      }

      const response = await axios.post(`${API}/companies`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const createdCompanyId = response.data.id;
      if (createdCompanyId) {
        navigate(`/dashboard/companies/${createdCompanyId}`);
      } else {
        navigate('/dashboard/companies');
      }
    } catch (err) {
      setApiError(err.response?.data?.detail || 'Failed to create company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputBase = 'w-full bg-[#0b1310] border rounded-lg px-3 py-2 text-white text-sm focus:outline-none transition-colors';
  const inputDefault = 'border-gray-700 focus:border-[#059669]';
  const inputError = 'border-red-500 focus:border-red-500';

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/dashboard/companies')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 text-sm"
          data-testid="add-company-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Companies
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-7 h-7 text-[#059669]" />
          <h1 className="text-2xl font-bold text-white">Add New Company</h1>
        </div>
        <p className="text-gray-400 text-sm mb-8">
          Register a new US company. We&apos;ll set everything up on your dashboard so you can manage formation, compliance, and more.
        </p>

        {apiError && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4 text-sm text-red-400" data-testid="add-company-error">
            {apiError}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-5 bg-[#111827] border border-gray-800 rounded-lg p-6"
          data-testid="add-company-form"
        >
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Company Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              className={`${inputBase} ${errors.company_name ? inputError : inputDefault}`}
              placeholder="e.g. Acme Technologies LLC"
              data-testid="company-name-input"
            />
            {errors.company_name && (
              <p className="mt-1 text-sm text-red-400">{errors.company_name}</p>
            )}
          </div>

          {/* Entity Type + State of Formation */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Entity Type <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.entity_type}
                onChange={(e) => handleChange('entity_type', e.target.value)}
                className={`${inputBase} ${errors.entity_type ? inputError : inputDefault}`}
                data-testid="entity-type-select"
              >
                <option value="">Select entity type</option>
                {ENTITY_TYPES.map(et => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
              {errors.entity_type && (
                <p className="mt-1 text-sm text-red-400">{errors.entity_type}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                State of Formation <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.state_of_formation}
                onChange={(e) => handleChange('state_of_formation', e.target.value)}
                className={`${inputBase} ${errors.state_of_formation ? inputError : inputDefault}`}
                data-testid="state-select"
              >
                <option value="">Select state</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state_of_formation && (
                <p className="mt-1 text-sm text-red-400">{errors.state_of_formation}</p>
              )}
            </div>
          </div>

          {/* Date of Formation */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date of Formation <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="date"
              value={formData.date_of_formation}
              onChange={(e) => handleChange('date_of_formation', e.target.value)}
              className={`${inputBase} ${inputDefault}`}
              data-testid="date-of-formation-input"
            />
          </div>

          {/* Business Address */}
          <div className="border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-4">
              Business Address <span className="text-gray-500">(optional)</span>
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                value={formData.business_address.street}
                onChange={(e) => handleAddressChange('business_address', 'street', e.target.value)}
                className={`${inputBase} ${inputDefault}`}
                placeholder="Street address"
                data-testid="business-street-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.business_address.city}
                  onChange={(e) => handleAddressChange('business_address', 'city', e.target.value)}
                  className={`${inputBase} ${inputDefault}`}
                  placeholder="City"
                  data-testid="business-city-input"
                />
                <select
                  value={formData.business_address.state}
                  onChange={(e) => handleAddressChange('business_address', 'state', e.target.value)}
                  className={`${inputBase} ${inputDefault}`}
                  data-testid="business-state-select"
                >
                  <option value="">State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                value={formData.business_address.zip}
                onChange={(e) => handleAddressChange('business_address', 'zip', e.target.value)}
                className={`${inputBase} ${inputDefault}`}
                placeholder="ZIP Code"
                data-testid="business-zip-input"
              />
            </div>
          </div>

          {/* Mailing Address */}
          <div className="border border-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-300">
                Mailing Address <span className="text-gray-500">(optional)</span>
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsBusinessAddress}
                  onChange={(e) => setSameAsBusinessAddress(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-[#059669] focus:ring-[#059669] focus:ring-offset-0"
                  data-testid="same-address-checkbox"
                />
                <span className="text-xs text-gray-400">Same as business</span>
              </label>
            </div>
            <div className={`space-y-3 ${sameAsBusinessAddress ? 'opacity-50 pointer-events-none' : ''}`}>
              <input
                type="text"
                value={sameAsBusinessAddress ? formData.business_address.street : formData.mailing_address.street}
                onChange={(e) => handleAddressChange('mailing_address', 'street', e.target.value)}
                className={`${inputBase} ${inputDefault}`}
                placeholder="Street address"
                data-testid="mailing-street-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={sameAsBusinessAddress ? formData.business_address.city : formData.mailing_address.city}
                  onChange={(e) => handleAddressChange('mailing_address', 'city', e.target.value)}
                  className={`${inputBase} ${inputDefault}`}
                  placeholder="City"
                  data-testid="mailing-city-input"
                />
                <select
                  value={sameAsBusinessAddress ? formData.business_address.state : formData.mailing_address.state}
                  onChange={(e) => handleAddressChange('mailing_address', 'state', e.target.value)}
                  className={`${inputBase} ${inputDefault}`}
                  data-testid="mailing-state-select"
                >
                  <option value="">State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                value={sameAsBusinessAddress ? formData.business_address.zip : formData.mailing_address.zip}
                onChange={(e) => handleAddressChange('mailing_address', 'zip', e.target.value)}
                className={`${inputBase} ${inputDefault}`}
                placeholder="ZIP Code"
                data-testid="mailing-zip-input"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
            style={{ backgroundColor: '#059669' }}
            data-testid="submit-company-btn"
          >
            {loading ? 'Creating Company...' : 'Create Company'}
          </button>
        </form>
      </div>
    </AppLayout>
  );
};

export default AddCompanyPage;
